import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../components/api/ApiClient';

function Notifications() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const tableRef = useRef(null);
    const username = JSON.parse(localStorage.getItem('user_data'))?.username;
    const [filterType, setFilterType] = useState('received');
    const [filterStatus, setFilterStatus] = useState('unread');
    const [filterLabel, setFilterLabel] = useState('بر اساس خوانده نشده');
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
    const [description, setDescription] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');

    const filters = [
        {label: 'بر اساس همه', type: 'all'},
        {label: 'بر اساس خوانده شده', type: 'read'},
        {label: 'بر اساس خوانده نشده', type: 'unread'},
        {label: 'بر اساس تایید شده', type: 'accepted'},
        {label: 'بر اساس رد شده', type: 'rejected'},
    ];

    const statusMap = {
        'send': 'در انتظار تایید',
        'accepted': 'تایید شده',
        'rejected': 'رد شده',
    };

    const showToast = (message, type = 'error') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        fetchNotifications(filterType, filterStatus);
    }, []);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast({ ...toast, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    useEffect(() => {
        if (!loading && notifications.length > 0 && window.jQuery && window.jQuery.fn.DataTable && tableRef.current) {
            if (window.jQuery.fn.DataTable.isDataTable(tableRef.current)) {
                window.jQuery(tableRef.current).DataTable().destroy();
            }
            return () => {
                if (window.jQuery && window.jQuery.fn.DataTable.isDataTable(tableRef.current)) {
                    try {
                        window.jQuery(tableRef.current).find('[data-bs-toggle="tooltip"]').tooltip('dispose');
                    } catch (e) {}
                }
            };
        }
    }, [notifications, loading]);

    const fetchNotifications = (t = filterType, f = filterStatus) => {
        setLoading(true);
        apiClient(`/internal-network/api/notification/?type=${t}&filter=${f}`)
            .then(data => {
                setNotifications(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching notifications:", err);
                setLoading(false);
            });
    };

    const handleFilterClick = (type) => {
        setFilterType(type);
        fetchNotifications(type, filterStatus);
    };

    const handleAction = async (action) => {
        if (!selectedNotif) return;
        setActionLoading(true);
        try {
            await apiClient('/internal-network/api/notification-action/', {
                method: 'POST',
                data: {
                    itemId: selectedNotif.id,
                    action: action,
                    description: description,
                },
            });
            if (action === 'accepted') {
                setSelectedNotif(prev => {
                    if (!prev) return null;
                    const newAction = {
                        status: action,
                        description: description,
                        company_name: username || '',
                        created_at_date: '-',
                        created_at_time: '-',
                    };
                    return {
                        ...prev,
                        action: [newAction, ...(prev.action || [])]
                    };
                });
                fetchNotifications(filterType, filterStatus);
                setDescription('');
                showToast('درخواست شما با موفقیت ارسال شد', 'success');
                return;
            }
            fetchNotifications(filterType, filterStatus);
            setDescription('');
            setSelectedNotif(null);
            showToast('درخواست شما با موفقیت ارسال شد', 'success');
        } catch (err) {
            console.error("Error performing action:", err);
            showToast(err.message || 'خطا در انجام عملیات', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const openModal = (notif) => {
        setSelectedNotif(notif);
        setDescription('');
        setActiveTab('details');
        apiClient('/internal-network/api/notification-option/', {
            method: 'POST',
            data: { itemId: notif.id },
        }).catch(() => {});
    };

    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
    };

    const typeMapping = {
        'foroush': 'فروش',
    };

    const getStatusBadge = (action, hasAccountingDoc) => {
        if (hasAccountingDoc) {
            return (
                <div className="d-flex flex-column gap-1">
                    <div className="badge bg-soft-success text-success">تایید شده</div>
                    <div className="badge bg-soft-info text-info" style={{ fontSize: '10px' }}>سند ثبت شده</div>
                </div>
            );
        }
        if (!action || action.length === 0) {
            return <div className="badge bg-soft-warning text-warning">در انتظار تایید</div>;
        }
        const lastAction = Array.isArray(action) ? action[0] : action;
        const status = typeof lastAction === 'string' ? lastAction : lastAction?.status;
        if (status === 'accepted') {
            return <div className="badge bg-soft-success text-success">تایید شده</div>;
        }
        if (status === 'rejected') {
            return <div className="badge bg-soft-danger text-danger">رد شده</div>;
        }
        return <div className="badge bg-soft-warning text-warning">در انتظار تایید</div>;
    };

    const getDateTimeAction = (action) => {
        if (!action || action.length === 0) {
            return '-';
        }
        const lastAction = Array.isArray(action) ? action[0] : action;
        return `${lastAction.created_at_date} ${lastAction.created_at_time}`;
    };

    const getInvoiceDate = (invoice) => invoice?.finalDate || invoice?.createDate || '-';
    const getInvoiceItems = (invoice) => invoice?.products || invoice?.productItems || [];

    return (
        <div className="row">
            {toast.show && (
                <div
                    aria-live="polite"
                    aria-atomic="true"
                    className="position-fixed p-3"
                    style={{ zIndex: 9999, top: 0, right: 0 }}
                >
                    <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true">
                        <div className="toast-header" style={{ direction: 'rtl' }}>
                            {toast.type === 'success' ? (
                                <span className="text-success me-2">✓</span>
                            ) : (
                                <span className="text-danger me-2">✗</span>
                            )}
                            <strong className="me-auto ms-2">
                                {toast.type === 'success' ? 'موفق' : 'خطا'}
                            </strong>
                        </div>
                        <div className="toast-body">{toast.message}</div>
                    </div>
                </div>
            )}
            <div className="col-lg-12">
                <div className="card stretch stretch-full">
                    <div className="card-header d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex" style={{gap: '10px'}}>
                            <button className={`btn btn-sm ${filterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterClick('all')}>
                                همه پیام ها
                            </button>
                            <button className={`btn btn-sm ${filterType === 'received' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterClick('received')}>
                                پیام های دریافتی
                            </button>
                            <button className={`btn btn-sm ${filterType === 'sent' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterClick('sent')}>
                                پیام های ارسالی
                            </button>
                        </div>
                        <div className="dropdown">
                            <button className="btn btn-sm btn-success dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                فیلتر {filterLabel}
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                                {filters.map(({label, type}) => (
                                    <li key={type}>
                                        <button className="dropdown-item text-end" type="button" onClick={() => {
                                            setFilterLabel(label);
                                            setFilterStatus(type);
                                            fetchNotifications(filterType, type);
                                        }}>
                                            {label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover" id="notificationList" ref={tableRef}>
                                <thead>
                                    <tr>
                                        <th>نوع پیام</th>
                                        <th>{filterType === 'sent' ? (
                                                'گیرنده'
                                            ) : filterType === 'received' ? (
                                                'فرستنده'
                                            ) : (
                                                'فرستنده / گیرنده'
                                            )}</th>
                                        <th>نوع ارسال</th>
                                        <th>شماره مدرک</th>
                                        <th>تاریخ مدرک</th>
                                        <th>تاریخ و زمان ارسال</th>
                                        <th>تاریخ و زمان تایید</th>
                                        <th>مبلغ پیام</th>
                                        <th>شماره مالیاتی</th>
                                        <th>وضعیت</th>
                                        <th>جزئیات پیام</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="12" className="text-center p-4">در حال بارگذاری...</td></tr>
                                    ) : notifications.length === 0 ? (
                                        <tr><td colSpan="12" className="text-center p-4">هیچ پیامی یافت نشد</td></tr>
                                    ) : (
                                        notifications.map((notif) => (
                                            <tr key={notif.id} className="single-item">
                                                <td>{notif.type === "foroush" & notif.sender_username == username ? 'فروش' : 'خرید'}</td>
                                                <td>{notif.sender_username === username ? notif.receiver_company_name : notif.company_name}</td>
                                                <td>{notif.type === "foroush" & notif.sender_username == username ? 'خروجی' : 'ورودی'}</td>
                                                <td>{notif.invoice?.invoiceNum || '-'}</td>
                                                <td dir="ltr" className='text-end'>{getInvoiceDate(notif.invoice)}</td>
                                                <td dir="ltr" className='text-end'>{notif.created_at}</td>
                                                <td dir="ltr" className='text-end'>{getDateTimeAction(notif.action)}</td>
                                                <td>{notif.invoice?.sumTotalPrice?.toLocaleString() || '-'}</td>
                                                <td>{notif.invoice?.uniqueIdentifier || '-'}</td>
                                                <td>{getStatusBadge(notif.action, notif.accounting_document_submitted)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-light-brand"
                                                        onClick={() => openModal(notif)}
                                                    >
                                                        مشاهده
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {selectedNotif && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">جزئیات پیام</h5>
                                <button type="button" className="btn-close" onClick={() => setSelectedNotif(null)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <ul className="nav nav-tabs mb-4">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                            onClick={() => handleTabSwitch('details')}
                                        >
                                            جزئیات
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                                            onClick={() => handleTabSwitch('history')}
                                        >
                                            گردش پیام
                                        </button>
                                    </li>
                                </ul>

                                {activeTab === 'details' && (
                                    <div className="card mb-4 border-0 shadow-none">
                                        <div className="card-body p-0">
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                <h3 className="fw-bold mb-0">{typeMapping[selectedNotif.type] || selectedNotif.type}</h3>
                                                {selectedNotif.accounting_document_submitted && (
                                                    <span className="badge bg-success fs-6">سند حسابداری ثبت شده</span>
                                                )}
                                            </div>

                                            <div className="row g-4 mb-4 text-dark">
                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                         {selectedNotif.sender_username === username ? (
                                                                <>
                                                                    <p className='fw-semibold me-2'>دریافت کننده :</p>
                                                                    <p>{selectedNotif.receiver_company_name || '-'}</p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className='fw-semibold me-2'>ارسال کننده :</p>
                                                                    <p>{selectedNotif.company_name || '-'}</p>
                                                                </>
                                                            )}
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">تاریخ و زمان ارسال :</span>
                                                        <span dir="ltr">{selectedNotif.created_at}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">وضعیت :</span>
                                                        <span>{statusMap[selectedNotif.action?.[0]?.status] || "در انتظار تایید"}</span>
                                                    </div>
                                                </div>

                                                <div className="col-12 border-top my-2"></div>

                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">شماره صورتحساب :</span>
                                                        <span>{selectedNotif.invoice?.invoiceNum}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">تاریخ صورتحساب :</span>
                                                        <span dir="ltr">{getInvoiceDate(selectedNotif.invoice)}</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">مبلغ کل :</span>
                                                        <span>{selectedNotif.invoice?.sumTotalPrice?.toLocaleString()} ریال</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fw-semibold me-2">شماره مالیاتی :</span>
                                                        <span className="text-break">{selectedNotif.invoice?.uniqueIdentifier}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {selectedNotif?.action?.[0]?.status !== 'accepted' && (
                                                <>
                                                    <div className="mb-4">
                                                        <textarea
                                                            className="form-control"
                                                            rows="3"
                                                            placeholder="توضیحات خود را اینجا وارد کنید..."
                                                            value={description}
                                                            onChange={(e) => setDescription(e.target.value)}
                                                            disabled={selectedNotif.sender_username == username}
                                                        ></textarea>
                                                    </div>
                                                    <div className="d-flex gap-3 mb-4">
                                                        <button
                                                            className="btn btn-danger flex-fill fw-bold py-2"
                                                            onClick={() => handleAction('rejected')}
                                                            disabled={selectedNotif.sender_username == username || actionLoading}
                                                        >
                                                            {actionLoading ? 'در حال ارسال...' : 'رد کردن'}
                                                        </button>
                                                        <button
                                                            className="btn btn-success flex-fill fw-bold py-2"
                                                            onClick={() => handleAction('accepted')}
                                                            disabled={selectedNotif.sender_username == username || actionLoading}
                                                        >
                                                            {actionLoading ? 'در حال ارسال...' : 'پذیرفتن'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {/* TEMP-HIDDEN: ثبت سند خرید (فرم خرید صورتحساب) — restore when needed
                                            {selectedNotif?.action?.[0]?.status === 'accepted' && (
                                                <div className="mb-4">
                                                    <button
                                                        className="btn btn-primary w-100 fw-bold py-2"
                                                        onClick={() => {
                                                            setSelectedNotif(null);
                                                            navigate(`/purchase/form/?notification_id=${selectedNotif.id}`);
                                                        }}
                                                    >
                                                        ثبت سند خرید
                                                    </button>
                                                </div>
                                            )}
                                            */}

                                            <h6 className="fw-bold mb-3 text-end">جدول کالا/خدمات صورتحساب</h6>
                                            <div className="table-responsive rounded border">
                                                <table className="table table-bordered mb-0 bg-white">
                                                    <thead className="bg-primary text-black">
                                                        <tr>
                                                            <th className="text-black">کد قلم</th>
                                                            <th className="text-black">عنوان قلم</th>
                                                            <th className="text-black">مقدار</th>
                                                            <th className="text-black">کد واحد</th>
                                                            <th className="text-black">نرخ</th>
                                                            <th className="text-black">مبلغ</th>
                                                            <th className="text-black">ارزش افزوده</th>
                                                            <th className="text-black">جمع کل</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {getInvoiceItems(selectedNotif.invoice).map((item, idx) => (
                                                            <tr key={idx}>
                                                                <td>{item.productId}</td>
                                                                <td className="text-wrap">{item.name}</td>
                                                                <td>{item.count || item.amount}</td>
                                                                <td>{item.measurement || '---'}</td>
                                                                <td>{item.unitPrice?.toLocaleString()}</td>
                                                                <td>{item.priceBeforeSale?.toLocaleString()}</td>
                                                                <td>{item.taxPrice?.toLocaleString()}</td>
                                                                <td>{item.totalPrice?.toLocaleString()}</td>
                                                            </tr>
                                                        ))}
                                                        {getInvoiceItems(selectedNotif.invoice).length === 0 && (
                                                             <tr>
                                                                 <td colSpan="8" className="text-center p-3">هیچ قلمی یافت نشد</td>
                                                             </tr>
                                                        )}
                                                    </tbody>
                                                    <tfoot className="bg-light fw-bold">
                                                        <tr>
                                                            <td colSpan="2" className="text-center">جمع کل</td>
                                                            <td>{selectedNotif.invoice?.sumWeight || ''}</td>
                                                            <td></td>
                                                            <td></td>
                                                            <td>{selectedNotif.invoice?.sumPriceBeforeSale?.toLocaleString()}</td>
                                                            <td>{selectedNotif.invoice?.sumTaxPrice?.toLocaleString()}</td>
                                                            <td>{selectedNotif.invoice?.sumTotalPrice?.toLocaleString()}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'history' && (
                                    <div className="card border-0 shadow-none">
                                        <div className="card-body p-0">
                                            <h5 className="fw-bold mb-3">جدول گزارش پیام</h5>
                                            {(!selectedNotif.action || selectedNotif.action.length === 0) ? (
                                                <div className="text-center py-4 text-muted">هیچ گردشی برای این پیام ثبت نشده است</div>
                                            ) : (
                                                <div className="table-responsive rounded border">
                                                    <table className="table table-bordered mb-0 bg-white">
                                                        <thead className="bg-primary">
                                                            <tr>
                                                                <th className="text-dark">اقدام کننده</th>
                                                                <th className="text-dark">تاریخ عمل</th>
                                                                <th className="text-dark">ساعت عمل</th>
                                                                <th className="text-dark">نوع عمل</th>
                                                                <th className="text-dark">توضیحات</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedNotif.action.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{item.company_name || '-'}</td>
                                                                    <td>{item.created_at_date || '-'}</td>
                                                                    <td>{item.created_at_time || '-'}</td>
                                                                    <td>{item.status === 'accepted' ? 'تایید شده' : item.status === 'rejected' ? 'رد شده' : item.status === 'send' ? 'ارسال شده' : item.status || '-'}</td>
                                                                    <td>{item.description || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setSelectedNotif(null)}>بستن</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Notifications;
